import { useRef, useState, useEffect, useMemo } from 'react';
import { ArrowRight, GitHub, Info } from 'react-feather';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import {
	ProjectModeSelector,
	type ProjectModeOption,
} from '../components/project-mode-selector';
import {
	MAX_AGENT_QUERY_LENGTH,
	SUPPORTED_IMAGE_MIME_TYPES,
	type ProjectType,
} from '@/api-types';
import { useFeature } from '@/features';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { usePaginatedApps } from '@/hooks/use-paginated-apps';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { AppCard } from '@/components/shared/AppCard';
import clsx from 'clsx';
import { useImageUpload } from '@/hooks/use-image-upload';
import { useDragDrop } from '@/hooks/use-drag-drop';
import { ImageUploadButton } from '@/components/image-upload-button';
import { ImageAttachmentPreview } from '@/components/image-attachment-preview';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

export default function Home() {
	const navigate = useNavigate();
	const { requireAuth } = useAuthGuard();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [projectMode, setProjectMode] = useState<ProjectType>('app');
	const [query, setQuery] = useState('');
	const [repoUrl, setRepoUrl] = useState('');
	const [isImportingRepository, setIsImportingRepository] = useState(false);
	const { user } = useAuth();
	const { isLoadingCapabilities, capabilities, getEnabledFeatures } =
		useFeature();

	const modeOptions = useMemo<ProjectModeOption[]>(() => {
		if (isLoadingCapabilities || !capabilities) return [];
		return getEnabledFeatures().map((def) => ({
			id: def.id,
			label:
				def.id === 'presentation'
					? 'Slides'
					: def.id === 'general'
						? 'General'
						: 'App',
			description: def.description,
		}));
	}, [capabilities, getEnabledFeatures, isLoadingCapabilities]);

	const showModeSelector = modeOptions.length > 1;

	useEffect(() => {
		if (isLoadingCapabilities) return;
		if (modeOptions.length === 0) {
			if (projectMode !== 'app') setProjectMode('app');
			return;
		}
		if (!modeOptions.some((m) => m.id === projectMode)) {
			setProjectMode(modeOptions[0].id);
		}
	}, [isLoadingCapabilities, modeOptions, projectMode]);

	const { images, addImages, removeImage, clearImages, isProcessing } =
		useImageUpload({
			onError: (error) => {
				console.error('Image upload error:', error);
				toast.error(error);
			},
		});

	const { isDragging, dragHandlers } = useDragDrop({
		onFilesDropped: addImages,
		accept: [...SUPPORTED_IMAGE_MIME_TYPES],
	});

	const placeholderPhrases = useMemo(
		() => [
			'ai-human work allocation cockpit for operations',
			'executive decision tracker with ai recommendations',
			'sales workflow orchestrator for reps and ai agents',
			'customer support triage and escalation copilot',
			'recruiting pipeline assistant with interviewer alignment',
			'product roadmap prioritization with cross-team signals',
			'finance close checklist with automated variance analysis',
			'go-to-market planning board with scenario simulation',
			'leadership alignment dashboard for ai transformation',
			'meeting-to-action system with ownership and follow-through',
		],
		[],
	);
	const [currentPlaceholderPhraseIndex, setCurrentPlaceholderPhraseIndex] =
		useState(0);
	const [currentPlaceholderText, setCurrentPlaceholderText] = useState('');
	const [isPlaceholderTyping, setIsPlaceholderTyping] = useState(true);

	const { apps, loading } = usePaginatedApps({
		type: 'public',
		defaultSort: 'popular',
		defaultPeriod: 'week',
		limit: 6,
	});

	// Discover section should appear only when enough apps are available and loading is done
	const discoverReady = useMemo(
		() => !loading && (apps?.length ?? 0) > 5,
		[loading, apps],
	);

	const handleCreateApp = (query: string, mode: ProjectType) => {
		if (query.length > MAX_AGENT_QUERY_LENGTH) {
			toast.error(
				`Prompt too large (${query.length} characters). Maximum allowed is ${MAX_AGENT_QUERY_LENGTH} characters.`,
			);
			return;
		}

		const encodedQuery = encodeURIComponent(query);
		const encodedMode = encodeURIComponent(mode);

		// Encode images as JSON if present
		const imageParam =
			images.length > 0
				? `&images=${encodeURIComponent(JSON.stringify(images))}`
				: '';
		const intendedUrl = `/chat/new?query=${encodedQuery}&projectType=${encodedMode}${imageParam}`;

		if (
			!requireAuth({
				requireFullAuth: true,
				actionContext: 'to create applications',
				intendedUrl: intendedUrl,
			})
		) {
			return;
		}

		// User is already authenticated, navigate immediately
		navigate(intendedUrl);
		// Clear images after navigation
		clearImages();
	};

	const handleImportRepository = async () => {
		const trimmedRepoUrl = repoUrl.trim();
		const intendedUrl = `/?importRepo=${encodeURIComponent(trimmedRepoUrl)}`;

		if (!trimmedRepoUrl) {
			toast.error('Repository URL is required');
			return;
		}

		if (
			!requireAuth({
				requireFullAuth: true,
				actionContext: 'to import repositories',
				intendedUrl,
			})
		) {
			return;
		}

		setIsImportingRepository(true);
		try {
			const response = await apiClient.importRepository({
				repoUrl: trimmedRepoUrl,
			});

			if (response.success && response.data?.requiresGitHubAuthorization && response.data.authUrl) {
				window.location.href = response.data.authUrl;
				return;
			}

			if (!response.success || !response.data?.agentId) {
				throw new Error(response.error?.message || 'Failed to import repository');
			}

			toast.success(
				`Imported ${response.data.filesImported} files from ${response.data.repoUrl}`,
			);
			setRepoUrl('');
			navigate(`/chat/${response.data.agentId}`);
		} catch (error) {
			const message =
				error instanceof Error ? error.message : 'Failed to import repository';
			toast.error(message);
		} finally {
			setIsImportingRepository(false);
		}
	};

	// Auto-resize textarea based on content
	const adjustTextareaHeight = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			const scrollHeight = textareaRef.current.scrollHeight;
			const maxHeight = 300; // Maximum height in pixels
			textareaRef.current.style.height =
				Math.min(scrollHeight, maxHeight) + 'px';
		}
	};

	useEffect(() => {
		adjustTextareaHeight();
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const importRepo = params.get('importRepo');
		if (importRepo) {
			setRepoUrl(importRepo);
		}

		const importStatus = params.get('repository_import');
		if (importStatus === 'success') {
			const filesImported = params.get('files');
			toast.success(
				filesImported
					? `Imported ${filesImported} files from GitHub`
					: 'Imported repository from GitHub',
			);
		} else if (importStatus === 'error') {
			toast.error(params.get('reason') || 'Failed to import repository');
		}
	}, []);

	// Typewriter effect
	useEffect(() => {
		const currentPhrase = placeholderPhrases[currentPlaceholderPhraseIndex];

		if (isPlaceholderTyping) {
			if (currentPlaceholderText.length < currentPhrase.length) {
				const timeout = setTimeout(() => {
					setCurrentPlaceholderText(
						currentPhrase.slice(
							0,
							currentPlaceholderText.length + 1,
						),
					);
				}, 100); // Typing speed
				return () => clearTimeout(timeout);
			} else {
				// Pause before erasing
				const timeout = setTimeout(() => {
					setIsPlaceholderTyping(false);
				}, 2000); // Pause duration
				return () => clearTimeout(timeout);
			}
		} else {
			if (currentPlaceholderText.length > 0) {
				const timeout = setTimeout(() => {
					setCurrentPlaceholderText(
						currentPlaceholderText.slice(0, -1),
					);
				}, 50); // Erasing speed
				return () => clearTimeout(timeout);
			} else {
				// Move to next phrase
				setCurrentPlaceholderPhraseIndex(
					(prev) => (prev + 1) % placeholderPhrases.length,
				);
				setIsPlaceholderTyping(true);
			}
		}
	}, [
		currentPlaceholderText,
		currentPlaceholderPhraseIndex,
		isPlaceholderTyping,
		placeholderPhrases,
	]);

	const discoverLinkRef = useRef<HTMLDivElement>(null);

	return (
		<div className="relative flex min-h-full w-full flex-col items-center pb-14">
			<div className="pointer-events-none fixed inset-0 z-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
			<LayoutGroup>
				<div className="w-full max-w-[1400px] overflow-hidden px-6 lg:px-10">
					<motion.div
						layout
						transition={{
							layout: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
						}}
						className={clsx(
							'relative z-10 flex max-w-4xl flex-col items-start',
							discoverReady
								? 'mt-28'
								: 'mt-[14vh] sm:mt-[16vh] md:mt-[18vh]',
						)}
					>
						<p className="editorial-kicker mb-5 inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
							<span className="h-1.5 w-1.5 bg-accent" />
							Open source · GitHub-native builds
						</p>
						<h1 className="editorial-title w-full text-[clamp(3rem,7vw,6.5rem)] text-white mb-7">
							Prompt to <span className="text-accent">app.</span>
							<br />
							Push to <span className="text-accent">main.</span>
						</h1>
						<p className="w-full max-w-2xl text-sm sm:text-base text-text-tertiary mb-9 leading-relaxed">
							Describe a concrete product outcome. W7S Build turns
							it into a GitHub-native app you can review, push,
							and ship.
						</p>

						<form
							method="POST"
							onSubmit={(e) => {
								e.preventDefault();
								const query = textareaRef.current!.value;
								handleCreateApp(query, projectMode);
							}}
							className="editorial-shell tracing-border flex z-10 flex-col w-full min-h-[180px] p-5 sm:p-6 transition-all duration-200"
						>
							<div
								className={clsx(
									'flex-1 flex flex-col relative',
									isDragging &&
										'ring-2 ring-accent/60 ring-offset-2 ring-offset-bg-3',
								)}
								{...dragHandlers}
							>
								{isDragging && (
									<div className="absolute inset-0 flex items-center justify-center bg-accent/10 backdrop-blur-sm z-30 pointer-events-none">
										<p className="text-accent font-medium">
											Drop images here
										</p>
									</div>
								)}
								<textarea
									className="w-full resize-none ring-0 z-20 outline-0 bg-transparent placeholder:text-text-tertiary/90 text-text-primary leading-7 text-base"
									name="query"
									value={query}
									placeholder={`Create a ${currentPlaceholderText}`}
									ref={textareaRef}
									onChange={(e) => {
										setQuery(e.target.value);
										adjustTextareaHeight();
									}}
									onInput={adjustTextareaHeight}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey) {
											e.preventDefault();
											const query =
												textareaRef.current!.value;
											handleCreateApp(query, projectMode);
										}
									}}
								/>
								{images.length > 0 && (
									<div className="mt-3">
										<ImageAttachmentPreview
											images={images}
											onRemove={removeImage}
										/>
									</div>
								)}
							</div>
							<div
								className={clsx(
									'flex items-center mt-5 pt-3 border-t border-border-primary/80',
									showModeSelector
										? 'justify-between'
										: 'justify-end',
								)}
							>
								{showModeSelector && (
									<ProjectModeSelector
										value={projectMode}
										onChange={setProjectMode}
										modes={modeOptions}
										className="flex-1"
									/>
								)}

								<div
									className={clsx(
										'flex items-center gap-2',
										showModeSelector && 'ml-4',
									)}
								>
									<ImageUploadButton
										onFilesSelected={addImages}
										disabled={isProcessing}
									/>
									<button
										type="submit"
										disabled={!query.trim()}
										className="bg-accent text-black p-1.5 border border-accent *:size-5 transition-all duration-200 hover:bg-[#fcd34d] disabled:opacity-50 disabled:cursor-not-allowed"
									>
										<ArrowRight />
									</button>
								</div>
							</div>
						</form>

						<form
							method="POST"
							onSubmit={(e) => {
								e.preventDefault();
								void handleImportRepository();
							}}
							className="mt-4 flex w-full flex-col gap-3 border border-border-primary/80 bg-bg-3/70 p-3 sm:flex-row sm:items-center"
						>
							<div className="flex min-w-0 flex-1 items-center gap-3">
								<GitHub className="size-4 shrink-0 text-text-tertiary" />
								<input
									type="text"
									value={repoUrl}
									onChange={(event) =>
										setRepoUrl(event.target.value)
									}
									placeholder="https://github.com/owner/repo"
									className="min-w-0 flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-tertiary"
									disabled={isImportingRepository}
								/>
							</div>
							<button
								type="submit"
								disabled={!repoUrl.trim() || isImportingRepository}
								className="inline-flex h-9 items-center justify-center gap-2 border border-accent px-3 text-xs font-medium text-accent transition-colors hover:bg-accent hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
							>
								<ArrowRight className="size-4" />
								{isImportingRepository ? 'Importing' : 'Import'}
							</button>
						</form>
					</motion.div>
				</div>

				<AnimatePresence>
					{images.length > 0 && (
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -10 }}
							className="w-full max-w-4xl px-4 sm:px-6"
						>
							<div className="editorial-shell flex items-start gap-2 px-4 py-3">
								<Info className="size-4 text-accent flex-shrink-0 mt-0.5" />
								<p className="text-xs text-text-tertiary leading-relaxed">
									<span className="font-medium text-text-secondary">
										Images Beta:
									</span>{' '}
									Images guide app layout and design but may
									not be replicated exactly. The coding agent
									cannot access images directly for app
									assets.
								</p>
							</div>
						</motion.div>
					)}
				</AnimatePresence>

				<AnimatePresence>
					{discoverReady && (
						<motion.section
							key="discover-section"
							layout
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: 'auto' }}
							exit={{ opacity: 0, height: 0 }}
							transition={{
								duration: 0.5,
								ease: [0.22, 1, 0.36, 1],
							}}
							className={clsx(
								'w-full max-w-6xl mx-auto px-4 sm:px-6 z-10',
								images.length > 0 ? 'mt-10' : 'mt-14 mb-8',
							)}
						>
							<div className="flex flex-col items-start">
								<p className="editorial-kicker mb-3">
									Community Library
								</p>
								<h2 className="editorial-title text-3xl sm:text-5xl text-white mb-3">
									Discover apps built by the community
								</h2>
								<div
									ref={discoverLinkRef}
									className="text-sm sm:text-base font-medium mb-6 text-accent hover:underline underline-offset-4 select-text cursor-pointer"
									onClick={() => navigate('/discover')}
								>
									View all
								</div>
								<motion.div
									layout
									transition={{ duration: 0.4 }}
									className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 w-full"
								>
									<AnimatePresence mode="popLayout">
										{apps.map((app) => (
											<AppCard
												key={app.id}
												app={app}
												onClick={() =>
													navigate(`/app/${app.id}`)
												}
												showStats={true}
												showUser={true}
												showActions={false}
											/>
										))}
									</AnimatePresence>
								</motion.div>
							</div>
						</motion.section>
					)}
				</AnimatePresence>
			</LayoutGroup>

			{/* Nudge towards Discover */}
			{user && (
				<CurvedArrow
					sourceRef={discoverLinkRef}
					target={{ x: 50, y: window.innerHeight - 60 }}
				/>
			)}
		</div>
	);
}

type ArrowProps = {
	/** Ref to the source element the arrow starts from */
	sourceRef: React.RefObject<HTMLElement | null>;
	/** Target point in viewport/client coordinates */
	target: { x: number; y: number };
	/** Curve intensity (0.1 - 1.5 is typical) */
	curvature?: number;
	/** Optional pixel offset from source element edge */
	sourceOffset?: number;
	/** If true, hides the arrow when the source is offscreen/not measurable */
	hideWhenInvalid?: boolean;
};

type Point = { x: number; y: number };

export const CurvedArrow: React.FC<ArrowProps> = ({
	sourceRef,
	target,
	curvature = 0.5,
	sourceOffset = 6,
	hideWhenInvalid = true,
}) => {
	const [start, setStart] = useState<Point | null>(null);
	const [end, setEnd] = useState<Point | null>(null);

	const rafRef = useRef<number | null>(null);
	const roRef = useRef<ResizeObserver | null>(null);

	const compute = () => {
		const el = sourceRef.current;
		if (!el) {
			setStart(null);
			setEnd(null);
			return;
		}

		const rect = el.getBoundingClientRect();
		if (!rect || rect.width === 0 || rect.height === 0) {
			setStart(null);
			setEnd(null);
			return;
		}

		const endPoint: Point = { x: target.x, y: target.y };

		// Choose an anchor on the source: midpoint of the side facing the target
		const centers = {
			right: { x: rect.right, y: rect.top + rect.height / 2 },
			left: { x: rect.left, y: rect.top + rect.height / 2 },
		};

		// Distances to target from each side center
		const dists = Object.fromEntries(
			Object.entries(centers).map(([side, p]) => [
				side,
				(p.x - endPoint.x) ** 2 + (p.y - endPoint.y) ** 2,
			]),
		) as Record<keyof typeof centers, number>;

		const bestSide = (Object.entries(dists).sort(
			(a, b) => a[1] - b[1],
		)[0][0] || 'right') as keyof typeof centers;

		// Nudge start point slightly outside the element for visual clarity
		const nudge = (
			p: Point,
			side: keyof typeof centers,
			offset: number,
		) => {
			switch (side) {
				case 'right':
					return { x: p.x + offset, y: p.y };
				case 'left':
					return { x: p.x - offset, y: p.y };
			}
		};

		const startPoint = nudge(centers[bestSide], bestSide, sourceOffset);

		setStart(startPoint);
		setEnd(endPoint);
	};

	// Throttle updates with rAF to avoid layout thrash
	const scheduleCompute = () => {
		if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
		rafRef.current = requestAnimationFrame(compute);
	};

	useEffect(() => {
		scheduleCompute();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [target.x, target.y, sourceRef.current]);

	useEffect(() => {
		const onScroll = () => scheduleCompute();
		const onResize = () => scheduleCompute();

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onResize);

		// Track source element size changes
		const el = sourceRef.current;
		if ('ResizeObserver' in window) {
			roRef.current = new ResizeObserver(() => scheduleCompute());
			if (el) roRef.current.observe(el);
		}

		scheduleCompute();

		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onResize);
			if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
			if (roRef.current && el) roRef.current.unobserve(el);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const d = useMemo(() => {
		if (!start || !end) return '';

		const dx = end.x - start.x;
		const dy = end.y - start.y;

		// Control points: bend the curve based on the primary axis difference.
		// This gives a nice S or C curve without sharp kinks.
		const cpOffset = Math.max(Math.abs(dx), Math.abs(dy)) * curvature;

		const c1: Point = {
			x: start.x + cpOffset * (dx >= 0 ? 1 : -1),
			y: start.y,
		};
		const c2: Point = {
			x: end.x - cpOffset * (dx >= 0 ? 1 : -1),
			y: end.y,
		};

		return `M ${start.x},${start.y} C ${c1.x},${c1.y} ${c2.x},${c2.y} ${end.x},${end.y}`;
	}, [start, end, curvature]);

	const hidden = hideWhenInvalid && (!start || !end);

	if (start && end && (end.y - start.y > 420 || start.x - end.x < 100)) {
		return null;
	}

	return (
		<svg
			aria-hidden="true"
			style={{
				position: 'fixed',
				inset: 0,
				width: '100vw',
				height: '100vh',
				pointerEvents: 'none',
				overflow: 'visible',
				zIndex: 9999,
				display: hidden ? 'none' : 'block',
			}}
		>
			<defs>
				<filter
					id="discover-squiggle"
					x="-20%"
					y="-20%"
					width="140%"
					height="140%"
				>
					<feTurbulence
						type="fractalNoise"
						baseFrequency="0.8"
						numOctaves="1"
						seed="3"
						result="noise"
					/>
					<feDisplacementMap
						in="SourceGraphic"
						in2="noise"
						scale="1"
						xChannelSelector="R"
						yChannelSelector="G"
					/>
				</filter>
				<marker
					id="discover-arrowhead"
					markerWidth="8"
					markerHeight="8"
					refX="7"
					refY="4"
					orient="auto"
					markerUnits="strokeWidth"
					opacity={0.2}
				>
					<path
						d="M 0 1.2 L 7 4"
						stroke="var(--color-text-tertiary)"
						strokeWidth="1.6"
						strokeLinecap="round"
						fill="none"
					/>
					<path
						d="M 0 6.8 L 7 4"
						stroke="var(--color-text-tertiary)"
						strokeWidth="1.2"
						strokeLinecap="round"
						fill="none"
					/>
				</marker>
			</defs>

			<path
				d={d}
				// stroke="var(--color-accent)"
				stroke="var(--color-text-tertiary)"
				strokeOpacity={0.2}
				strokeWidth={1.6}
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
				vectorEffect="non-scaling-stroke"
				markerEnd="url(#discover-arrowhead)"
			/>
			{/* Soft squiggle overlay for hand-drawn feel */}
			<g filter="url(#discover-squiggle)">
				<path
					d={d}
					// stroke="var(--color-accent)"
					stroke="var(--color-text-tertiary)"
					strokeOpacity={0.12}
					strokeWidth={1}
					fill="none"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeDasharray="8 6 4 9 5 7"
					vectorEffect="non-scaling-stroke"
				/>
			</g>
		</svg>
	);
};
